import { AxiosError, AxiosResponse } from 'axios';
import { DefaultTFuncReturn } from 'i18next';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ErrorInfo, GroupSearch } from '../../../api/model';
import * as sessionStorageUtils from '../../../utils/sessionStorage';
import PageNavi from '../../modules/PageNavi';
import { OrganizationDownload } from './OrganizationDownload';
import ErrorComponent from '../../atoms/ErrorComponent';
import NoDataComponent from '../../atoms/NoDataComponent';
import WaitComponent from '../../atoms/WaitComponent';

const N_DATA_PER_PAGE = 50;

// 一覧のソートキー
type SortKey = 'company_name' | 'group_name' | 'team_name' | 'user_name';

interface SortableThProps {
  label: DefaultTFuncReturn;
  onClick: () => void;
}

function SortableTh({ label, onClick }: SortableThProps) {
  return (
    <th>
      <button type="button" className="text-button" onClick={onClick}>
        {label}
        <i className="fa-solid fa-sort" />
      </button>
    </th>
  );
}

interface Props {
  data: AxiosResponse<GroupSearch, unknown> | undefined;
  isLoading: boolean;
  isError: boolean;
  error: AxiosError<ErrorInfo> | null;
}

function OrganizationList({ data, isLoading, isError, error }: Props) {
  const { t, i18n } = useTranslation();

  // 現在表示中のページ番号
  const [currentPage, setCurrentPage] = useState(sessionStorageUtils.get('Groups.CurrentPage', 1));

  // ソート情報
  const [sortInfo, setSortInfo] = useState(
    sessionStorageUtils.get('Groups.SortInfo', { key: 'user_id_text' as SortKey, isDisc: false })
  );

  // 初回ロード中かどうか
  const refIsFirstLoading = useRef(true);

  // 一覧データをソートしたもの
  const sortedGroups = useMemo(
    () =>
    [...(data?.data.group_list_items ?? [])].sort((a, b) => {
        const aAny = a as { [key: string]: string | undefined };
        const bAny = b as { [key: string]: string | undefined };
        const multiValue = sortInfo.isDisc ? -1 : 1;
        if (aAny[sortInfo.key] !== bAny[sortInfo.key]) {
          // 第一ソートキー
          return ((aAny[sortInfo.key] ?? '') > (bAny[sortInfo.key] ?? '') ? 1 : -1) * multiValue;
        }
        return 1;
      }),
    [sortInfo, data]
  );

  // 一覧のソートキーを変更する関数
  const changeSortKey = useCallback((key: SortKey) => {
    setSortInfo((pre) => {
      const next = { key, isDisc: pre.key === key && !pre.isDisc };
      sessionStorageUtils.set('Groups.SortInfo', next);
      return next;
    });
    setCurrentPage(1);
  }, []);

  // 現在ページを変更する関数
  const changeCurrentPage = useCallback(
    (next: number) => {
      sessionStorageUtils.set('Groups.CurrentPage', next);
      setCurrentPage(next);
    },
    [setCurrentPage]
  );

  // データが変更されたときの動作
  useEffect(() => {
    if (data?.data.group_list_items != null) {
      if (refIsFirstLoading.current) {
        // 初回ロード完了時は初回ロードが完了したことを設定する
        refIsFirstLoading.current = false;
      } else {
        // 初回ロード以外にデータが変更されたときは、現在ページを 1 ページにする
        setCurrentPage(1);
      }
    }
  }, [data?.data.group_list_items]);

  // 現在のページに表示する一覧
  const filteredGroups = useMemo(
    () => sortedGroups.slice(N_DATA_PER_PAGE * (currentPage - 1), N_DATA_PER_PAGE * currentPage),
    [currentPage, sortedGroups]
  );

  const companyName = (index: number) => {
    if (index === 0) {
      return filteredGroups[index].company_name;
    }

    if (filteredGroups[index].company_name === filteredGroups[index - 1].company_name) {
      return '';
    }
    return filteredGroups[index].company_name;
  };

  const groupName = (index: number) => {
    if (index === 0) return filteredGroups[index].group_name;

    if (filteredGroups[index].group_name === filteredGroups[index - 1].group_name) {
      return '';
    }
    return filteredGroups[index].group_name;
  };

  // データ取得中
  if (isLoading) {
    return <WaitComponent />;
  }

  // エラー表示
  if (isError) {
    return <ErrorComponent error={error} />;
  }

  // データが空の場合
  if (data == null || data.data.group_list_items == null) {
    return <NoDataComponent />;
  }

  return (
    <>
      <div className="ttlsep prolist" style={{ marginTop: '40px' }}>
        <div className="left">
          <h3 className="trig">
            <span>{t('グループ一覧')}</span>
          </h3>
        </div>
        <div className="right">
          <ul>
            <li>
              <Link to={`/${i18n.language}/organizations/tree`}>
                <i className="fa-solid fa-pen-to-square" />
                {t('組織編集')}
              </Link>
            </li>
            <li>
              <OrganizationDownload />
            </li>
          </ul>
        </div>
      </div>

      <div className="wfr in-pgnavi mb0">
        <table className="table42 mb30">
          <tbody>
            <tr>
              <SortableTh label={t('会社名')} onClick={() => changeSortKey('company_name')} />
              <SortableTh label={t('グループ名')} onClick={() => changeSortKey('group_name')} />
              <SortableTh label={t('チーム名')} onClick={() => changeSortKey('team_name')} />
              <SortableTh label={t('ユーザー')} onClick={() => changeSortKey('user_name')} />
              <th aria-label="test" />
            </tr>
            {filteredGroups?.map((group, i) => (
              <tr
                key={`group-${group.company_name || ''}-${group.group_name || ''}-${group.team_name || ''}-${
                  group.user_name || ''
                }`}
              >
                <td>{companyName(i)}</td>
                <td>{groupName(i)}</td>
                <td>{group.team_name}</td>
                <td>{group.user_name}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <PageNavi
          current={currentPage}
          pageCount={Math.ceil(data.data.group_list_items.length / (N_DATA_PER_PAGE * 1.0))}
          onChangePage={changeCurrentPage}
        />
      </div>
    </>
  );
}

export { OrganizationList };
